import { CloseFullscreen, OpenInFull } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement, useState } from "react";
import { useParams } from "react-router-dom";

import { FlexBox } from "~frontend/components/FlexBox";
import { PageHeader } from "~frontend/components/PageHeader";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { SubmitButton } from "~frontend/components/forms";
import theme, { palette } from "~frontend/theme";

import { BrandForm } from "./BrandForm";
import { CampaignsTab } from "./CampaignsTab";
import { useTenDlcContext } from "./TenDlcProvider";

const ExpandableBox = ({
  children,
  expanded,
  collapsedWidth = "30%",
}: {
  children: ReactElement | ReactElement[];
  expanded: boolean;
  collapsedWidth?: string;
}) => (
  <Box
    sx={{
      padding: `${theme.spacing(2)} ${theme.spacing(5)} ${theme.spacing(4)}`,
      backgroundColor: palette.white,
      width: expanded ? "100%" : collapsedWidth,
      minWidth: "400px",
    }}
  >
    {children}
  </Box>
);

const ContainerHeader = ({
  title,
  expanded,
  onClick,
}: {
  title: string;
  expanded: boolean;
  onClick: (open: boolean) => void;
}) => (
  <FlexBox alignItems="center" gap={1}>
    <IconButton size="small" onClick={() => onClick(!expanded)}>
      {expanded ? <CloseFullscreen /> : <OpenInFull />}
    </IconButton>
    <Typography variant="h1Bold">{title}</Typography>
  </FlexBox>
);

export const Forms = (): ReactElement => {
  const { campaignRegistrationId } = useParams<{
    brandRegistrationId: string;
    campaignRegistrationId?: string;
  }>();

  const { isAnyFormModified, isAnyFormSubmitting, handleUpdateAll } =
    useTenDlcContext()!;

  const [expandedSection, setExpandedSection] = useState<"brand" | "campaigns">(
    campaignRegistrationId ? "campaigns" : "brand"
  );

  return (
    <>
      <FlexBox justifyContent="space-between" alignItems="center">
        <PageHeader>10DLC Registration</PageHeader>
        <SubmitButton
          disabled={!isAnyFormModified || isAnyFormSubmitting}
          submitText="Save All Changes"
          tooltipText="Save all changes made to the brand and campaign(s), if records can be modified"
          loadingText="Saving..."
          isSubmitting={isAnyFormSubmitting}
          onClick={handleUpdateAll}
        />
      </FlexBox>
      <FlexBox gap={2}>
        <RouteLeavingGuard when={isAnyFormModified} />
        <ExpandableBox expanded={expandedSection === "brand"}>
          <BrandForm
            header={
              <ContainerHeader
                title="Brand"
                expanded={expandedSection === "brand"}
                onClick={(open: boolean) =>
                  setExpandedSection(open ? "brand" : "campaigns")
                }
              />
            }
          />
        </ExpandableBox>
        <ExpandableBox expanded={expandedSection === "campaigns"}>
          <CampaignsTab
            header={
              <ContainerHeader
                title="Campaigns"
                expanded={expandedSection === "campaigns"}
                onClick={(open: boolean) =>
                  setExpandedSection(open ? "campaigns" : "brand")
                }
              />
            }
          />
        </ExpandableBox>
      </FlexBox>
    </>
  );
};
